import React, { useMemo, useState } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import Tooltip from '../General/Tooltip';

import { getReferrals, getUsername } from '../../Helpers/user_helpers';
import { getUserTiers, getUserTierDisplay } from '../../Helpers/tier_helpers';
import { getPrettyPriceDisplay, getPrettyDate } from '../../Helpers/formatting';

const EarningsPortalReferrals = props => {
  const { user, payoutsAreLoading } = props;

  const [referralsVisible, setReferralsVisible] = useState(5);

  const referrals = useMemo(() => {
    const { user } = props;
    const referrals = getReferrals(user);
    const referralEarnings = _.get(user, 'payouts.payout_summaries.referralTotals', {});
    const sortedReferrals = _.concat(_.orderBy(referrals, 'createdAt', 'desc'));
    return sortedReferrals.map(referral => {
      const earnings = _.get(referralEarnings, referral.id, {});
      return {
        ...referral,
        ...earnings
      };
    });
  }, [user]);
  const hasNoReferrals = referrals.length === 0;
  const hasMoreReferrals = referrals.length > referralsVisible;
  const gridTemplateColumns = '1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr';
  const inviteUrl = `/invite/${getUsername(user)}`;

  const summaryData = useMemo(() => {
    const total = referrals.reduce((acc, referral) => {
      acc.user_payout_total += referral.user_payout_total || 0;
      acc.user_payout_total_locked += referral.user_payout_total_locked || 0;
      acc.user_payout_total_paid += referral.user_payout_total_paid || 0;
      return acc;
    }, {
      user_payout_total: 0,
      user_payout_total_locked: 0,
      user_payout_total_paid: 0
    });

    const rawTierResponse = JSON.parse(user.profile?.tier?.raw || '{}');

    return {
      ...total,
      referralCredits: referrals.reduce((sum, referral) => sum + (getUserTiers().find(t => t.tier === referral.tier.tier)?.referral_credits || 0), 0),
      referralBonusPoints: rawTierResponse['Referral Bonus (20)'] || 0,
    };
  }, [user, referrals]);

  if (payoutsAreLoading) return null;
  return (
    <div className='earnings-portal-referrals-outer'>
      <div className='earnings-portal-referrals-inner'>
        <div className='earnings-portal-section-header'>
          <div>
            <div className='earnings-portal-section-title'>Referrals</div>
            <div className='earnings-portal-section-subtitle'>
              Earn up to $1000 for each referred creator. <Link to={inviteUrl}>More info here</Link>
            </div>
          </div>
          <div>
            <Link
              to={inviteUrl}
              className='earnings-portal-section-button'
            >
              Refer & Earn
            </Link>
          </div>
        </div>

        <div className='earnings-portal-table'>
          <div className='earnings-portal-table-header' style={{ gridTemplateColumns }}>
            <div className='earnings-portal-table-cell left'>Joined</div>
            <div className='earnings-portal-table-cell left'>Name</div>
            <div className='earnings-portal-table-cell left'>Best Tier</div>
            <div className='earnings-portal-table-cell left'>Date</div>
            <div className='earnings-portal-table-cell'>Bonus</div>
            <div className='earnings-portal-table-cell'>Pending</div>
            <div className='earnings-portal-table-cell'>Paid</div>
            <div className='earnings-portal-table-cell'>Referral Credits</div>
            <div className='earnings-portal-table-cell'>Bonus Points</div>
          </div>
          <div className='earnings-portal-summary-row' style={{ gridTemplateColumns }}>
            <div className='earnings-portal-table-cell left'>Total</div>
            <div/>
            <div/>
            <div/>
            <div className='earnings-portal-table-cell'>
              {getPrettyPriceDisplay(summaryData.user_payout_total)}
            </div>
            <div className='earnings-portal-table-cell'>
              {getPrettyPriceDisplay(summaryData.user_payout_total_locked)}
            </div>
            <div className='earnings-portal-table-cell'>
              {getPrettyPriceDisplay(summaryData.user_payout_total_paid)}
            </div>
            <div className='earnings-portal-table-cell'>
              {summaryData.referralCredits}
            </div>
            <div className='earnings-portal-table-cell'>
              {summaryData.referralBonusPoints}
            </div>
          </div>
          {!hasNoReferrals && (
            <div className='earnings-portal-table-body'>
              {referrals.slice(0, referralsVisible).map((referral, idx) => {
                const { createdAt, name, username, tier, user_payout_total = 0, user_payout_total_locked = 0, user_payout_total_paid = 0, last_payout_date } = referral;
                const referralCredits = getUserTiers().find(t => t.tier === tier.tier)?.referral_credits || 0;
                const hasBeenDemoted = tier.tier !== tier.bestTier;

                return (
                  <div key={idx} className='earnings-portal-table-row' style={{ gridTemplateColumns }}>
                    <div className='earnings-portal-table-cell left'>{getPrettyDate(createdAt, 'MM/DD/YY', 'MM/DD/YY')}</div>
                    <a href={`/${username}`} target='_blank' rel='noopener nofollow noreferrer' className='earnings-portal-table-cell left'>
                      {name}
                    </a>
                    <div className='earnings-portal-table-cell left'>
                      {getUserTierDisplay(tier.bestTier)}
                    </div>
                    <div className={'earnings-portal-table-cell left'}>
                      {last_payout_date ? getPrettyDate(last_payout_date, 'MM/DD/YY', 'MM/DD/YY') : 'N/A'}
                    </div>
                    <div className='earnings-portal-table-cell'>{getPrettyPriceDisplay(user_payout_total)}</div>
                    <div className='earnings-portal-table-cell'>{getPrettyPriceDisplay(user_payout_total_locked)}</div>
                    <div className='earnings-portal-table-cell'>{getPrettyPriceDisplay(user_payout_total_paid)}</div>
                    <div className='earnings-portal-table-cell'>
                      {hasBeenDemoted ? (
                        <Tooltip
                          message={`Referral credits are based on this creator's current tier: ${getUserTierDisplay(tier.tier)}`}
                        >
                          {referralCredits}
                        </Tooltip>
                      ) : (
                        referralCredits
                      )}
                    </div>
                    <div className='earnings-portal-table-cell'>—</div>
                  </div>
                );
              })}

              {referrals.length > 5 && !payoutsAreLoading && (
                <div className='earnings-portal-table-row' style={{ gridTemplateColumns: '1fr' }}>
                  <div className='show-more' onClick={() => (hasMoreReferrals ? setReferralsVisible(referralsVisible + 5) : setReferralsVisible(5))}>
                    {hasMoreReferrals ? 'Show More' : 'Show Less'}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

EarningsPortalReferrals.propTypes = {
  user: PropTypes.object.isRequired,
  payoutsAreLoading: PropTypes.bool.isRequired
};

export default EarningsPortalReferrals;
